import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
    Divider,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import { camelCaseToTitleCase, capitalizeName } from '../../../shared/utils';

export const formatHirePeriod = (hours: number) => {
    if (hours === 24) return `${hours / 24} day`;
    if (hours === 48) return `${(hours - 24) / 24} night`;
    if (hours > 24) return `${(hours - 24) / 24} nights`;
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
};

export const formatMaxHirePeriod = (hours: number) => {
    if (hours === 24) return `${hours / 24} day`;
    if (hours === 48) return `${(hours - 24) / 24} night`;
    if (hours > 24) return `${(hours - 24) / 24} nights`;
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
};

type HireRangeExperience = {
    type?: string;
    minHireHours?: number;
    maxHireHours?: number;
};

export const formatHireRange = (experience: HireRangeExperience) => {
    if (experience?.type === 'commercial') {
        const minLabel =
            (experience?.minHireHours ?? 0) >= 24
                ? String((experience?.minHireHours ?? 0) / 24)
                : formatHirePeriod(experience?.minHireHours ?? 0);
        const maxDays = (experience?.maxHireHours ?? 0) / 24;
        const dayLabel = experience?.maxHireHours === 24 ? 'day' : 'days';
        return `${minLabel} - ${maxDays} ${dayLabel}`;
    }

    return `${formatHirePeriod(
        experience?.minHireHours ?? 0,
    )} - ${formatMaxHirePeriod(experience?.maxHireHours ?? 0)}`;
};

const IndividualExperienceDetails = ({
    value,
    component,
}: {
    value: any;
    component?: string;
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const DetailTypography = ({ label, value }: any) => (
        <Grid>
            <Typography
                sx={{
                    color: textColor,
                    paddingRight: '5px',
                    fontSize: isMobile ? '14px' : '14px',
                }}
            >
                {label}: {value}{' '}
                <span style={{ paddingLeft: '5px' }}>
                    <FiberManualRecordIcon sx={{ fontSize: '4px' }} />
                </span>
            </Typography>
        </Grid>
    );

    const CapacityTypography = ({
        label,
        value,
        index,
        totalCount,
        isIpad,
    }: any) => (
        <Typography
            sx={{
                color: textColor,
                marginBottom: '8px',
                fontSize: isMobile ? '14px' : '14px',
            }}
        >
            {label}: {value}
            {index !== totalCount - 1 && !isIpad && (
                <span style={{ paddingLeft: '20px' }}>
                    <FiberManualRecordIcon sx={{ fontSize: '4px' }} />
                </span>
            )}
        </Typography>
    );

    const days = [
        { day: 'monday', value: value?.monday },
        { day: 'tuesday', value: value?.tuesday },
        { day: 'wednesday', value: value?.wednesday },
        { day: 'thursday', value: value?.thursday },
        { day: 'friday', value: value?.friday },
        { day: 'saturday', value: value?.saturday },
        { day: 'sunday', value: value?.sunday },
    ];

    const availableDays = days.filter?.((i: any) => i.value === true);

    const capacity: any = {
        dedicatedDesks: value?.details?.desks,
        privateOffices: value?.details?.privateOffices,
        meetingRooms: value?.details?.meetingRooms,
    };

    const fullAddressPickup = `${value?.streetAddress} ${value?.suburb} ${value?.postcode} ${value?.state} ${value?.countryCode}`;
    const fullAddressDropoff = `${value?.details?.dropoffStreetAddress} ${value?.details?.dropoffSuburb} ${value?.details?.dropoffPostcode} ${value?.details?.dropoffState} ${value?.details?.dropoffCountryCode}`;

    const rules = [];

    const theme = useTheme();

    if (value?.details?.minimumLicenceRequired) {
        rules.push(
            `Minimum licence required: ${capitalizeName(
                value.details.minimumLicenceRequired,
            )}`,
        );
    }
    if (value?.details?.minimumDriversAge) {
        rules.push(`Minimum driver's age: ${value.details.minimumDriversAge}`);
    }

    if (value?.details?.petsAllowed === false) {
        rules.push('Pets not allowed');
    }

    if (value?.details?.smokingAllowed === false) {
        rules.push('Smoking, vaping, e-cigarettes not allowed');
    }

    if (value?.details?.rules) {
        rules.push(value.details.rules);
    }

    const textColor =
        component && component === 'aiGuestReservation'
            ? 'white'
            : `${theme.palette.midGrey.main}`;

    return (
        <>
            <Grid
                sx={{
                    width: isMobile || isIpad ? '100%' : '88%',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {value?.type === 'property' || value?.type === 'boat' ? (
                    <Grid
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                        }}
                    >
                        {/* Conditional rendering for non-commercial properties */}
                        {!['commercial', 'office space'].includes(
                            value?.details?.subType,
                        ) && (
                            <>
                                <DetailTypography
                                    label='Sleeps'
                                    value={value?.details.overnightGuests}
                                />
                                <DetailTypography
                                    label='Bedrooms'
                                    value={value?.details.bedrooms}
                                />
                                <DetailTypography
                                    label='Beds'
                                    value={value?.details.beds}
                                />
                            </>
                        )}

                        {/* Always display Bathrooms and Max Guests */}
                        <DetailTypography
                            label='Bathrooms'
                            value={value?.details.baths}
                        />
                        <Grid>
                            <Typography
                                sx={{
                                    color: textColor,
                                    paddingRight: '5px',
                                    fontSize: isMobile ? '14px' : '14px',
                                }}
                            >
                                Max {value?.details.maxGuests} guests on the{' '}
                                {value?.type}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : value?.type === 'vehicle' ? (
                    <Grid
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                        }}
                    >
                        {/* General Vehicle Details */}
                        <DetailTypography
                            label={`${value?.details.make} ${
                                value?.details.model
                            } ${
                                value?.details.subType === 'car'
                                    ? capitalizeName(value?.details?.bodyType)
                                    : ''
                            } ${value?.details?.year || ''}`}
                            value=''
                        />

                        <DetailTypography
                            label='Transmission'
                            value={capitalizeName(
                                value?.details.transmission === 'cvt'
                                    ? 'CVT'
                                    : value?.details.transmission,
                            )}
                        />

                        <DetailTypography
                            label='Fuel Type'
                            value={capitalizeName(value?.details.fuelType)}
                        />

                        {/* Motorbike Specific Details */}
                        {value.details.subType === 'motorbike' && (
                            <DetailTypography
                                label='Engine CC'
                                value={value?.details?.engineCC}
                            />
                        )}

                        {/* Car Specific Details */}
                        {value.details.subType === 'car' && (
                            <>
                                <DetailTypography
                                    label='Seats'
                                    value={value?.details.numberOfSeats}
                                />
                                <DetailTypography
                                    label='Number of Doors'
                                    value={value?.details.numberOfDoors}
                                />
                            </>
                        )}
                    </Grid>
                ) : value?.type === 'commercial' &&
                  ['office', 'house'].includes(value.details.subType) ? (
                    <Grid
                        sx={{
                            display: 'flex',
                            flexDirection: isIpad ? 'column' : 'row',
                            flexWrap: 'wrap',
                            gap: '5px',
                            paddingTop: isIpad ? '10px' : '0px',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Title */}
                        <Typography
                            sx={{
                                color: textColor,
                                paddingRight: '5px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: 'bold',
                                fontSize: '14px',
                            }}
                        >
                            Total Capacity
                        </Typography>

                        {/* Capacity Values */}
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: isIpad ? 'column' : 'row',
                                flexWrap: 'wrap',
                                gap: isIpad ? '0px' : '30px',
                            }}
                        >
                            {capacity ? (
                                Object.keys(capacity)
                                    .filter((key) => capacity[key] > 0)
                                    .map((key, index) => {
                                        const label =
                                            key === 'privateOffices' &&
                                            value?.details?.subType === 'house'
                                                ? 'Rooms'
                                                : camelCaseToTitleCase(key);
                                        const totalCount = Object.keys(
                                            capacity,
                                        ).filter(
                                            (key) => capacity[key] > 0,
                                        ).length;

                                        return (
                                            <CapacityTypography
                                                key={key}
                                                label={label}
                                                value={capacity[key]}
                                                index={index}
                                                totalCount={totalCount}
                                                isIpad={isIpad}
                                            />
                                        );
                                    })
                            ) : (
                                <Typography
                                    sx={{
                                        color: textColor,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Please select the dates
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Typography
                        sx={{
                            color: textColor,
                        }}
                    >
                        {value?.details.quantity} x {value?.name}
                    </Typography>
                )}
            </Grid>
            {/* <Divider
                        sx={{ width: isMobile || isIpad ? '100%' : '88%' }}
                    /> */}
            <Grid
                sx={{
                    height: 'auto',
                    width: isMobile || isIpad ? '100%' : '88%',
                    paddingTop: '30px',
                    // backgroundColor: 'gray',
                }}
            >
                <Typography
                    sx={{
                        color: textColor,
                        paddingBottom: '10px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                    }}
                >
                    {value?.type === 'property'
                        ? 'Property'
                        : value?.type === 'boat'
                          ? 'Boat'
                          : value?.type === 'vehicle'
                            ? 'Vehicle'
                            : value?.type === 'commercial'
                              ? capitalizeName(value?.details?.subType)
                              : 'Activity'}{' '}
                    Description
                </Typography>
                <Typography
                    sx={{
                        color: textColor,
                        fontSize: '14px',
                    }}
                >
                    {value?.description}
                </Typography>
                <Typography
                    sx={{
                        color: textColor,
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                    }}
                >
                    Reservation Information
                </Typography>
                <Typography
                    sx={{
                        color: textColor,
                        fontSize: '14px',
                    }}
                >
                    <ul style={{ paddingLeft: '20px' }}>
                        <li>
                            This experience accepts {value?.visitType}{' '}
                            reservations only for{' '}
                            {availableDays.map((name, i) => (
                                <span key={i}>
                                    {i > 0 && ','}{' '}
                                    {name.day.charAt(0).toUpperCase() +
                                        name.day.slice(1)}
                                </span>
                            ))}
                        </li>

                        <li>
                            This experience{' '}
                            {value?.publicHolidays
                                ? 'accepts'
                                : "doesn't accept"}{' '}
                            reservations for public holidays.
                        </li>

                        <li>
                            {value?.type === 'commercial'
                                ? `Access from ${value?.checkinTime} to ${value?.checkoutTime}`
                                : `Checkin from ${value?.checkinTime} and checkout before ${value?.checkoutTime}`}
                        </li>

                        <li>
                            The min/max hire period is {formatHireRange(value)}
                        </li>
                    </ul>
                </Typography>

                {value?.type === 'vehicle' && rules?.length > 0 && (
                    <>
                        {' '}
                        <Typography
                            sx={{
                                color: textColor,
                                paddingBottom: '10px',
                                paddingTop: '10px',
                                fontWeight: 'bold',
                                fontSize: '14px',
                            }}
                        >
                            Hiring Rules
                        </Typography>
                        <Typography
                            sx={{
                                color: textColor,
                                paddingBottom: '10px',
                                fontSize: '14px',

                                listStyle:
                                    rules.length === 1 ? 'none' : 'default',
                            }}
                        >
                            {rules.map((rule: any, index: any) => (
                                <li key={index}>{rule}</li>
                            ))}
                        </Typography>
                    </>
                )}
            </Grid>{' '}
            {value?.type === 'vehicle' && (
                <Grid
                    sx={{
                        height: 'auto',
                        paddingTop: isIpad ? '10px' : '30px',
                        width: isMobile || isIpad ? '100%' : '88%',
                        // backgroundColor: 'blue',
                    }}
                >
                    <Divider
                        sx={{
                            width: '100%',
                        }}
                    />
                    <Typography
                        sx={{
                            color: textColor,
                            paddingTop: '30px',
                            paddingBottom: '10px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                        }}
                    >
                        Pick Up{' '}
                        {value.details.dropoffLocationSameAsPickup &&
                            'and Drop Off '}
                        Location
                    </Typography>{' '}
                    <Typography
                        sx={{
                            color: textColor,
                            fontSize: '14px',
                        }}
                    >
                        {' '}
                        {fullAddressPickup}
                    </Typography>
                    {!value.details.dropoffLocationSameAsPickup && (
                        <>
                            {' '}
                            <Typography
                                sx={{
                                    color: textColor,
                                    paddingTop: '30px',
                                    paddingBottom: '10px',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                }}
                            >
                                Drop Off Location
                            </Typography>{' '}
                            <Typography
                                sx={{
                                    color: textColor,
                                    fontSize: '14px',
                                }}
                            >
                                {' '}
                                {fullAddressDropoff}
                            </Typography>
                        </>
                    )}
                </Grid>
            )}
        </>
    );
};

export default IndividualExperienceDetails;
